<template>
	<main>
		<LoadingIcon v-if="isLoading && !error" alignment="center" />

		<div v-if="!isLoading && !isEmpty(loan) && !error">
			<div class="loan-container">
				<AlertTypeIcon alertType="Loan Listing" />
				<LoanInfo :loan="loan" />
				<QuickActionsMenu
					:lightButton="true"
					:lightButtonIcon="['far', 'ellipsis-h']"
					:menus="populateQuickActions(loan.uwmInd)"
					@deleteLoan="handleDeleteLoan(loan.loanId)"
					@sendToBlink="
						$emit(
							'sendToBlinkClick',
							loan.loanId,
							loan.subjectPropertyAddress.addressLine1,
						)
					"
					@viewInEase="handleEaseLinkClick(loan.easeLoanUrl)"
				/>
			</div>
			<div class="tabs">
				<div
					role="button"
					aria-label="Details"
					@click="setLoanDetailsView('Details')"
					class="tab"
					:class="{ selected: view === 'Details' }"
				>
					Details
				</div>
				<div
					v-if="loan.loanId"
					role="button"
					aria-label="Settings"
					@click="setLoanDetailsView('Settings')"
					class="tab"
					:class="{ selected: view === 'Settings' }"
				>
					Settings
				</div>
			</div>

			<div v-if="view === 'Details'">
				<form class="details-form" v-on:submit.prevent>
					<fieldset :disabled="mainProfileIsInternalUser">
						<div class="loan-form-container" @change="success = false">
							<div>
								<FormDropdowns
									label="UWM Loan"
									name="uwm-loan"
									:disabled="true"
									v-model="loan.uwmInd"
									:options="yesNoOptions"
								/>
							</div>
							<div>
								<FormDropdowns
									label="Paid in Full"
									name="paid-in-full"
									v-model="loan.paidInFullInd"
									:options="yesNoOptions"
									:placeholderDisable="true"
								/>
							</div>

							<div>
								<FormInputs
									ref="address"
									label="Address Line 1"
									name="subject-property-address-line-1"
									:maxLength="50"
									type="text"
									v-model="loan.subjectPropertyAddress.addressLine1"
									:disabled="loan.uwmInd"
									:error="invalidAddress && invalidError"
								/>
								<div v-if="invalidAddress && invalidError" class="error">
									Required
								</div>
							</div>

							<div>
								<FormInputs
									label="Address Line 2"
									name="subject-property-address-line-2"
									:maxLength="50"
									type="text"
									v-model="loan.subjectPropertyAddress.addressLine2"
									:disabled="loan.uwmInd"
								/>
							</div>
							<div>
								<FormInputs
									label="Unit Number"
									name="subject-property-address-unit-number"
									:maxLength="50"
									type="text"
									:disabled="loan.uwmInd"
									v-model="loan.subjectPropertyAddress.unitNumber"
								/>
							</div>
							<div>
								<FormInputs
									label="City"
									name="subject-property-city"
									:maxLength="30"
									type="text"
									v-model="loan.subjectPropertyAddress.city"
									:disabled="loan.uwmInd"
									:error="invalidAddress && invalidError && invalidCity"
								/>
								<div
									v-if="invalidAddress && invalidError && invalidCity"
									class="error"
								>
									Required
								</div>
							</div>

							<div>
								<FormDropdowns
									label="State"
									name="subject-property-state"
									v-model="loan.subjectPropertyAddress.state"
									:options="states"
									:disabled="loan.uwmInd"
									:placeholderDisable="false"
									:error="
										invalidAddress &&
										invalidError &&
										!loan.subjectPropertyAddress.state
									"
								/>
								<div
									v-if="
										invalidAddress &&
										invalidError &&
										!loan.subjectPropertyAddress.state
									"
									class="error"
								>
									Required
								</div>
							</div>

							<div>
								<FormInputs
									ref="zipCode"
									label="Zip Code"
									name="subject-property-zip"
									type="text"
									:maxLength="5"
									:mask="[/\d/, /\d/, /\d/, /\d/, /\d/]"
									v-model="loan.subjectPropertyAddress.zipCode"
									:disabled="loan.uwmInd"
									:error="invalidError && invalidZipCode"
								/>
								<div v-if="invalidError && invalidZipCode" class="error">
									Must be 5 digits
								</div>
							</div>

							<div>
								<FormInputs
									label="County"
									name="subject-property-county"
									:maxLength="30"
									type="text"
									v-model="loan.subjectPropertyAddress.county"
									:disabled="loan.uwmInd"
								/>
							</div>

							<div>
								<FormInputs
									label="Loan Product"
									name="product"
									:maxLength="30"
									type="text"
									v-model="loan.product"
									:disabled="loan.uwmInd"
								/>
							</div>

							<div>
								<FormDatepicker
									label="Closing Date"
									name="closing-date"
									id="closing-date"
									v-model="loan.closingDate"
									placeholder="Select a date..."
									:fullMonthName="true"
									maximumView="month"
									:disabled="loan.uwmInd"
									:clear-button="!loan.uwmInd"
								/>
							</div>

							<div>
								<FormDropdowns
									label="Loan Purpose"
									name="loan-purpose"
									v-model="loan.purpose"
									:options="dropDownOptions.loanPurposeTypes"
									:disabled="loan.uwmInd"
									:placeholderDisable="false"
									:placeholderValue="{
										purposeId: 0,
										purposeDesc: null,
										purposeType: null,
									}"
								/>
							</div>

							<div class="loan-number-container">
								<a
									class="loan-detail-link"
									:class="{ disabled: !loan.uwmInd }"
									:disabled="!loan.uwmInd"
									:href="loan.easeLoanUrl"
									target="_blank"
									><font-awesome-icon
										:icon="['far', 'external-link-alt']"
									/>View in EASE</a
								>
								<FormInputs
									label="Loan Number"
									name="loan-number"
									:maxLength="20"
									type="text"
									v-model="loan.loanNumber"
									:disabled="loan.uwmInd"
								/>
							</div>

							<div>
								<FormDropdowns
									label="Property Type"
									name="property-type"
									v-model="loan.propertyType"
									:options="dropDownOptions.propertyTypes"
									:disabled="loan.uwmInd"
									:placeholderDisable="false"
									:placeholderValue="{
										propertyTypeId: 0,
										propertyTypeDesc: null,
									}"
								/>
							</div>

							<div>
								<FormMaskedInputs
									:label="HELOC || HELOCProduct ? 'Draw Amount' : 'Loan Amount'"
									:name="HELOC || HELOCProduct ? 'draw-amount' : 'loan-amount'"
									type="text"
									v-model="loan.loanAmount"
									mask="maskCurrency"
									:disabled="loan.uwmInd"
									data-test="loan/test-amount"
								/>
							</div>

							<div>
								<FormMaskedInputs
									label="Note Rate"
									name="interest-rate"
									type="text"
									v-model="loan.interestRate"
									mask="maskPercentage"
									:disabled="loan.uwmInd"
								/>
							</div>

							<div v-if="HELOC || HELOCProduct">
								<FormMaskedInputs
									label="Total Line Amount"
									name="total-line-amount"
									type="text"
									v-model="loan.totalLineAmount"
									mask="maskCurrency"
									:disabled="loan.uwmInd"
									data-test="total-line-amount"
								/>
							</div>

							<div>
								<FormDropdowns
									label="Occupancy Type"
									name="occupancy-type"
									v-model="loan.occupancyType"
									:options="dropDownOptions.occupancyTypes"
									:disabled="loan.uwmInd"
									:placeholderDisable="false"
									:placeholderValue="{
										occupancyTypeId: 0,
										occupancyTypeDesc: null,
									}"
								/>
							</div>

							<div>
								<FormMaskedInputs
									label="Appraised Value"
									name="appraised-value"
									type="text"
									v-model="loan.appraisedValue"
									mask="maskCurrency"
									:disabled="loan.uwmInd"
								/>
							</div>

							<div>
								<FormMaskedInputs
									label="Loan Term"
									name="loan-term"
									type="text"
									v-model="loan.term"
									mask="maskYears"
									:disabled="loan.uwmInd"
								/>
							</div>

							<div>
								<FormMaskedInputs
									:label="HELOC || HELOCProduct ? 'CLTV' : 'Loan to Value'"
									:name="HELOC || HELOCProduct ? 'cltv' : 'closing-ltv'"
									type="text"
									v-model="loan.closingLTV"
									mask="maskPercentage"
									:disabled="loan.uwmInd"
									data-test="loan-to-value/cltv"
								/>
							</div>

							<div>
								<FormDropdowns
									label="Loan Type"
									name="loan-type"
									v-model="loan.type"
									:options="dropDownOptions.loanTypes"
									:disabled="loan.uwmInd"
									:placeholderDisable="false"
									:placeholderValue="{
										typeId: 0,
										typeDesc: null,
									}"
								/>
							</div>

							<div v-if="HELOC || HELOCProduct">
								<FormMaskedInputs
									label="HCLTV"
									name="hcltv"
									type="text"
									v-model="loan.hcltv"
									mask="maskPercentage"
									:disabled="loan.uwmInd"
									data-test="hcltv"
								/>
							</div>

							<div v-if="HELOC || HELOCProduct">
								<FormDropdowns
									label="Lien Position"
									name="lien-position"
									v-model="loan.lienPosition"
									:options="dropDownOptions.lienPositions"
									:disabled="loan.uwmInd"
									data-test="lien-position"
									:placeholderDisable="false"
									:placeholderValue="{
										amortizationTypeId: 0,
										amortizationTypeDesc: null,
									}"
								/>
							</div>

							<div>
								<FormDropdowns
									label="Amortization Type"
									name="amortization-type"
									v-model="loan.amortizationType"
									:options="dropDownOptions.amortizationTypes"
									:disabled="loan.uwmInd"
									:placeholderDisable="false"
									:placeholderValue="{
										amortizationTypeId: 0,
										amortizationTypeDesc: null,
									}"
								/>
							</div>
						</div>
					</fieldset>
					<OverviewModalFooter
						:success="success"
						:primaryButtonDisabled="mainProfileIsInternalUser"
						:waiting="waiting"
						:isSaveModal="true"
						@handleClick="handleSaveLoan"
						@close="handleCancelSaveLoan"
					/>
				</form>
			</div>

			<div class="settings-view" v-else-if="view === 'Settings'">
				<LoanSettings
					:loanId="loan.loanId"
					@configureAlerts="handleConfigureAlerts"
				/>
			</div>
		</div>

		<div v-if="error">
			<FallBack
				class="error-message"
				iconStyle="fal"
				icon="exclamation-triangle"
				:message="error"
			/>
		</div>
	</main>
</template>

<script>
import { isEmpty, cloneDeep } from 'lodash';
import { isValidString } from '@/util/isValidString';
import { mapActions, mapState } from 'vuex';

import AlertTypeIcon from '../AlertTypeIcon.vue';
import FallBack from '../../../components/global/FallBack.vue';
import FormDatepicker from '../../../components/global/FormDatepicker.vue';
import FormDropdowns from '../../../components/global/FormDropdowns.vue';
import FormInputs from '../../../components/global/FormInputs.vue';
import FormMaskedInputs from '../../../components/global/FormMaskedInputs.vue';
import LoadingIcon from '../../../components/global/LoadingIcon.vue';
import QuickActionsMenu from '../QuickActionsMenu.vue';
import LoanInfo from './LoanInfo.vue';
import OverviewModalFooter from './OverviewModalFooter.vue';
import LoanSettings from './LoanSettings.vue';
import loansService from '@/services/contacts/loans-service.js';

import { VIEWS, NEW_LOAN } from './constants';

export default {
	name: 'LoanDetails',
	components: {
		AlertTypeIcon,
		FallBack,
		FormDatepicker,
		FormDropdowns,
		FormInputs,
		FormMaskedInputs,
		LoadingIcon,
		QuickActionsMenu,
		LoanInfo,
		OverviewModalFooter,
		LoanSettings,
	},
	data() {
		return {
			loan: {},
			isLoading: false,
			invalidError: false,
			error: false,
			success: false,
			timerId: null,
			waiting: false,
			defaultErrorMessage:
				'Unable to update the loan details. Please try again in a few minutes.',
			yesNoOptions: [
				{
					name: 'Yes',
					value: true,
				},
				{
					name: 'No',
					value: false,
				},
			],
		};
	},
	props: {
		leadId: Number,
		loanId: {
			type: Number,
			default: null,
		},
		contactId: Number,
		entityId: Number,
		history: Array,
		blinkDisabled: Boolean,
		loggedInUserId: Number,
	},
	computed: {
		...mapState({
			loans: (state) => state.ccContacts.loans.listWithDetails,
			view: (state) => state.ccContacts.loanDetails.view,
			states: (state) => state.ccContacts.leads.states,
			dropDownOptions: (state) => state.ccContacts.loans.dropDownOptions,
			mainProfileIsInternalUser: (state) =>
				state.profileSwitcher.mainProfileSelector.isInternalUser,
		}),
		invalidAddress() {
			const { state } = this.loan.subjectPropertyAddress;
			return (this.invalidCity || isEmpty(state)) && this.invalidAddressLine1;
		},
		invalidZipCode() {
			let zipCode = this.loan.subjectPropertyAddress.zipCode;
			if (zipCode === null) return false;
			return zipCode.length !== 0 && zipCode.length !== 5;
		},
		invalidCity() {
			return !isValidString(this.loan.subjectPropertyAddress.city);
		},
		invalidAddressLine1() {
			return !isValidString(this.loan.subjectPropertyAddress.addressLine1);
		},
		HELOC() {
			return this.loan.type.typeDesc
				? this.loan.type.typeDesc.includes('HELOC')
				: false;
		},
		HELOCProduct() {
			const HELOCProductIDs = [435, 397, 828, 830, 835, 836];
			return HELOCProductIDs.includes(this.loan.productId);
		},
	},
	methods: {
		...mapActions([
			'getLoanById',
			'setLoanDetailsView',
			'getAllStates',
			'getGetAllLoanDropDownOptions',
		]),
		/**
		 * when we use a function in the template that is imported from outside
		 * the component it needs to be included in the methods object
		 */
		// deepcode ignore VueIncorrectMethodType: see comment above
		isEmpty,
		populateQuickActions(isUWM) {
			return [
				[
					{
						icon: ['far', 'file-import'],
						text: 'Send to Blink+',
						action: 'sendToBlink',
						disabled:
							this.blinkDisabled ||
							this.mainProfileIsInternalUser ||
							this.loggedInUserId !== this.contactId,
					},
					{
						icon: ['far', 'external-link-alt'],
						text: 'View in EASE',
						action: 'viewInEase',
						disabled: !isUWM,
					},
				],
				[
					{
						icon: ['far', 'trash-alt'],
						text: 'Delete Loan',
						action: 'deleteLoan',
						iconColor: 'red',
						disabled: this.mainProfileIsInternalUser,
					},
				],
			];
		},
		handleEaseLinkClick(url) {
			window.open(url, '_blank');
		},
		scrollErrorIntoView() {
			let errorRef;
			if (this.invalidAddress) {
				errorRef = this.$refs.address.$el;
			} else {
				errorRef = this.$refs.zipCode.$el;
			}
			errorRef.scrollIntoView({ behavior: 'smooth' });
		},
		// eslint-disable-next-line consistent-return
		async handleSaveLoan() {
			this.error = false;
			this.success = false;
			this.invalidError = this.invalidAddress || this.invalidZipCode;
			if (this.invalidError) {
				setTimeout(() => {
					this.scrollErrorIntoView();
				}, 50);
			}

			if (!this.invalidError) {
				this.waiting = true;
				const payload = {
					loanId: this.loan.loanId,
					leadId: this.leadId,
					amortizationTypeId: this.loan.amortizationType.amortizationTypeId,
					appraisedValue: parseFloat(
						this.loan.appraisedValue.replace(
							// eslint-disable-next-line no-useless-escape
							/[^0-9\.]/g,
							'',
						),
					),
					closingDate: this.loan.closingDate,
					closingLOContactId: this.loan.closingLOContactId
						? this.loan.closingLOContactId
						: this.contactId,
					closingLOEntityId: this.loan.closingLOEntityId
						? this.loan.closingLOEntityId
						: this.entityId,
					// eslint-disable-next-line no-useless-escape
					closingLTV: parseFloat(this.loan.closingLTV.replace(/[^0-9\.]/g, '')),
					hcltv: parseFloat(
						this.loan.hcltv ? this.loan.hcltv.replace(/[^0-9\.]/g, '') : null,
					),
					interestRate: parseFloat(
						this.loan.interestRate.replace(
							// eslint-disable-next-line no-useless-escape
							/[^0-9\.]/g,
							'',
						),
					),
					lienPositionId: this.loan.lienPosition.lienPositionId,
					// eslint-disable-next-line no-useless-escape
					loanAmount: parseFloat(this.loan.loanAmount.replace(/[^0-9\.]/g, '')),
					loanNumber: this.loan.loanNumber,
					occupancyTypeId: this.loan.occupancyType.occupancyTypeId,
					paidInFullInd: this.loan.paidInFullInd,
					product: this.loan.product,
					propertyTypeId: this.loan.propertyType.propertyTypeId,
					totalLineAmount: parseFloat(
						this.loan.totalLineAmount.replace(/[^0-9\.]/g, ''),
					),
					typeId: this.loan.type.typeId,
					purposeId: this.loan.purpose.purposeId,
					subjectPropertyAddress: {
						addressLine1: this.loan.subjectPropertyAddress.addressLine1,
						addressLine2: this.loan.subjectPropertyAddress.addressLine2,
						unitNumber: this.loan.subjectPropertyAddress.unitNumber,
						city: this.loan.subjectPropertyAddress.city,
						county: this.loan.subjectPropertyAddress.county,
						state: this.loan.subjectPropertyAddress.state,
						zipCode: this.loan.subjectPropertyAddress.zipCode,
					},
					term: parseInt(
						this.loan.term
							? // eslint-disable-next-line no-useless-escape
							  this.loan.term.replace(/[^0-9\.]/g, '')
							: null,
					),
					uwmInd: this.loan.uwmInd,
				};
				// backend throws a 400 if loanId is null
				if (payload.loanId === null) {
					delete payload.loanId;
				}
				try {
					const response = await loansService.saveLoanDetails(payload);
					if (response.loans) {
						if (response.loans.isSuccess) {
							if (this.loan.loanId === null) {
								// if this is a new loan there won't be a loanId, so use the one returned
								const newLoanId = parseInt(response.loans.id, 10);
								this.loan.loanId = newLoanId;
								this.$emit('newLoanAdded', newLoanId);
							}
						} else if (response.loans.errorMessage === 'Duplicate Loan.') {
							return this.$emit('onDuplicateLoanError', VIEWS.DUPLICATE_LOAN);
						} else {
							this.error = this.defaultErrorMessage;
						}
					}

					setTimeout(() => {
						this.waiting = false;
						this.success = true;
					}, 250);
					setTimeout(() => {
						this.success = false;
					}, 2000);
				} catch (error) {
					this.error = this.defaultErrorMessage;
					this.waiting = false;
				}
			}
		},
		handleCancelSaveLoan() {
			this.$emit('onCloseLoanDetails');
		},
		handleDeleteLoan(loanId) {
			this.$emit('onDeleteLoanClick', loanId);
		},
		handleConfigureAlerts(currentPreference) {
			this.$emit('configureAlerts', currentPreference);
		},
		async getLoanDetails(loanId) {
			if (loanId) {
				this.isLoading = true;
				const foundLoan = this.loans.find((loan) => loan.loanId === loanId);
				this.loan = foundLoan && cloneDeep(foundLoan);
				if (isEmpty(this.loan)) {
					try {
						this.loan = await this.getLoanById(loanId);
					} catch (error) {
						this.error = error;
					}
				}
				// Cast some values as string since masked input requires it.
				this.loan.loanAmount = this.loan.loanAmount
					? this.loan.loanAmount.toString()
					: '';
				this.loan.interestRate = this.loan.interestRate
					? this.loan.interestRate.toString()
					: '';
				this.loan.appraisedValue = this.loan.appraisedValue
					? this.loan.appraisedValue.toString()
					: '';
				this.loan.closingLTV = this.loan.closingLTV
					? this.loan.closingLTV.toString()
					: '';
				this.loan.hcltv = this.loan.hcltv ? this.loan.hcltv.toString() : '';
				this.loan.totalLineAmount = this.loan.totalLineAmount
					? this.loan.totalLineAmount.toString()
					: '';
				this.timerId = setTimeout(() => (this.isLoading = false), 250);
			} else {
				this.loan = cloneDeep(NEW_LOAN);
			}
			this.$emit('setUWMLoanInd', this.loan.uwmInd);
		},
	},
	async created() {
		if (isEmpty(this.states)) {
			this.isLoading = true;
			try {
				await this.getAllStates();
			} catch (error) {
				this.error =
					'Unable to load the loan details. Please try again in a few minutes.';
			} finally {
				this.isLoading = false;
			}
		}
		if (!this.dropDownOptions) {
			this.isLoading = true;
			try {
				await this.getGetAllLoanDropDownOptions();
			} catch (error) {
				this.error =
					'Unable to load the loan details. Please try again in a few minutes.';
			} finally {
				this.isLoading = false;
			}
		}
		if (isEmpty(this.loan)) {
			await this.getLoanDetails(this.loanId);
		}
	},
	beforeDestroy() {
		if (this.timerId) clearTimeout(this.timerId);
	},
};
</script>

<style lang="scss" scoped>
.tabs {
	display: flex;
	align-items: center;
	border-bottom: 1px solid $border-color-light;
	@include rem('height', 60px);

	.tab {
		font-weight: 500;
		cursor: pointer;
		@include rem('margin-left', 20px);

		&.selected {
			color: $brand-blue;
			border-bottom: 2px solid $brand-blue;
			@include rem('padding', 20px 0 18px 0);
		}
	}
}

.loan-container {
	display: grid;
	grid-template-columns: 46px auto 44px;
	@include rem('padding', 17px 20px);
	@include rem('grid-gap', 15px);
	border-bottom: 1px solid $border-color-light;
	background: $white;

	/deep/ .btn-quick-actions {
		height: 44px;
		width: 44px;
	}
}

.details-form {
	fieldset {
		display: block;
		margin: 0;
		padding: 0;
		border: none;
	}

	.loan-details-label {
		color: $b360-color--gray__darkest;
		font-weight: 500;
		@include rem('margin-bottom', 16px);
	}

	.loan-form-container {
		@include rem('padding', 20px);

		.error {
			color: $error;
			font-style: italic;
		}
	}

	.loan-number-container {
		position: relative;
	}

	.loan-detail-link {
		@include typo--roboto__medium;
		color: $brand-blue;
		text-decoration: none;
		position: absolute;
		right: 0;
		top: 0;

		&.disabled {
			color: $b360-color--gray__medium-plus;
			cursor: not-allowed;
		}

		svg {
			@include rem('margin-right', 5px);
		}
	}

	@media only screen and (min-width: 769px) {
		.form-inputs {
			/deep/ .b360-form-input {
				width: 270px;
				background-color: $white;
				&:disabled {
					background-color: $b360-color--disabled-bg;
					cursor: not-allowed;
				}
			}
		}

		.loan-form-container {
			display: grid;
			grid-template-columns: auto auto;
			@include rem('grid-gap', 20px);
		}
	}
}

.error-message {
	border: none;
}
</style>
